import React, { MouseEvent, useEffect, useRef, useState, KeyboardEvent, ChangeEvent } from 'react';
import { ClickAwayListener, Avatar, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControlLabel, IconButton, Link, List, ListItem, ListItemAvatar, ListItemButton, ListItemIcon, ListItemText, Paper, Popper, Radio, RadioGroup, styled, Table, TableBody, TableCell, tableCellClasses, TableContainer, TableFooter, TableHead, TablePagination, TableRow, Tooltip, Typography, ButtonGroup, Grow, MenuList, MenuItem, Stack, Alert, Chip, TooltipProps, tooltipClasses, Menu } from '@mui/material';
import { ButtonUnstyled } from '@mui/base';
import { collection, getCountFromServer, doc, DocumentData, endBefore, getDocs, getFirestore, limit, limitToLast, orderBy, Query, query, QueryDocumentSnapshot, QuerySnapshot, setDoc, startAfter, startAt, where, or, and } from 'firebase/firestore';
import TablePaginationActions from '@mui/material/TablePagination/TablePaginationActions';
import { DateTime, Interval } from 'luxon';
import { ArrowDropDownRounded, ArrowForwardIosRounded, CalendarTodayRounded, ChatBubbleRounded, CheckRounded, CompareArrowsRounded, DeleteRounded, EditRounded, FaceRounded, InfoRounded, ManageAccountsRounded, MopedRounded, PersonAddRounded, PersonRemoveRounded, PersonRounded, PersonSearchRounded, RunCircleRounded, SearchRounded, VillaRounded } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { queryEmployee } from '../../utils/queryEmployee';
import { Employee } from '../../types/Employee';
import employeeConverter from '../../converters/employeeConverter';
import { EmployeeForm } from './EmployeeForm';
import { CleanButton } from '../CleanButton';
import { queryEmployees } from '../../utils/queryEmployees';
import { useAppDispatch, useAppSelector } from '../../store';
import { setEmployeePerPage } from '../../store/slices/appSlice';
import { Search, SearchIconWrapper, StyledInputBase } from '../Search';
import { useDebouncedCallback } from 'use-debounce';
import { queryLastVacation } from '../../utils/queryVacations';
import { EmployeeResetUserAccount } from './EmployeeResetUserAccount';
import { EmployeeUpdateProfilePhoto } from './EmployeeUpdateProfilePhoto';
import { EmployeeTransitionForm } from './EmployeeTransitionForm';
import { queryMessageThreads } from '../../utils/queryMessages';
import { PERMISSIONS_EMPLOYEES, PERMISSIONS_USERS } from '../../security/Permission';
import { SecureComponent } from '../../security/SecureComponent';
import { ROLES_PERMISSIONS_MAPPING } from '../../security/Role';

const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#f5f5f9',
    color: 'rgba(0, 0, 0, 0.87)',
    maxWidth: 400,
    minWidth: 350,
    fontSize: theme.typography.pxToRem(12),
    border: '1px solid #dadde9',
  },
}));

type DeleteDialogProps = {
  open: boolean,
  employees: Array<any>,
  onCancel: () => void,
  onConfirm: () => void,
}
const DeleteDialog = (props: DeleteDialogProps) => {
  const { open, employees, onCancel, onConfirm } = props;

  return (
    <Dialog
      sx={{ '& .MuiDialog-paper': { width: '80%', maxHeight: 435 }, backdropFilter: 'blur(5px)' }}
      maxWidth="xs"
      open={open}
    >
      <DialogTitle>Delete Selected Employee{employees.length > 1 ? 's' : ''}</DialogTitle>
      <DialogContent dividers>
        <List>
          {employees.map((d) => (
            <ListItem key={d.uid} dense>
              <ListItemAvatar>
                <Avatar sizes="small" alt={d.name} src={d.picture} />
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Typography sx={{ fontWeight: 700, lineHeight: 'normal', mt: 0.4 }}>
                    {d.name}
                  </Typography>
                }
                secondary={
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, lineHeight: 'normal' }}>
                    {d.idNumber}
                  </Typography>
                }
              />
            </ListItem>
          ))}
        </List>
      </DialogContent>
      <DialogActions>
        <Button autoFocus onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={onConfirm}>Confirm</Button>
      </DialogActions>
    </Dialog>
  )
};

export const EmployeeDataTable = () => {
  const db = getFirestore();

  const navigate = useNavigate();

  const dispatch = useAppDispatch();
  const employeePerPage = useAppSelector((s) => s.app.employeePerPage);
  const authState = useAppSelector(s => s.auth);

  // const adminOnly = authState.URB && authState.URB.roles.some((r: string) => ['super', 'admin'].includes(r));
  
  const addButtonOptions = ['Chief Supervisors', 'Supervisor', 'Employee'];
  const anchorAddButtonRef = React.useRef<HTMLDivElement>(null);
  const [selectedAddButtonAction, setSelectedAddButtonAction] = useState(2);
  const [addButtonOpen, setAddButtonOpen] = useState(false);

  const [page, setPage] = useState(0);
  const [firstVisibleEmployee, setFirstVisibleEmployee] = useState<QueryDocumentSnapshot | null>(null);
  const [lastVisibleEmployee, setLastVisibleEmployee] = useState<QueryDocumentSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [selected, setSelected] = React.useState<readonly string[]>([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [search, setSearch] = useState('');
  
  const [editMenuAnchorEl, setEditMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [openEditForm, setOpenEditForm] = useState(false);
  const [openTransitionEmployee, setOpenTransitionEmployee] = useState(false);
  const [openResetAccount, setOpenResetAccount] = useState(false);
  const [openUpdatePhoto, setOpenUpdatePhoto] = useState(false);
  
  const editMenuOpen = Boolean(editMenuAnchorEl);

  const setEmployeeList = async (querySnapShots: QuerySnapshot<Employee>) => {
    Promise.allSettled(querySnapShots.docs.map(async (doc) => {
        const d = doc.data();
        try {
          // Get last vacation leave
          const vacationInfo = await queryLastVacation(d.idNumber);
          d.metadata = {
            ...d.metadata,
            vacationInfo
          };

          // Get messagre thread
          const idNumber: string = authState.idNumber;
          if (authState.isSignedIn && d.idNumber !== idNumber) {
            const messageThreads = await queryMessageThreads(idNumber, [idNumber, d.idNumber]);
            d.metadata = {
              ...d.metadata,
              messageThreads,
            };
          }
          
          if (d.supervisor)
            d.supervisorData = await queryEmployee(d.supervisor);
          if (d.chiefSupervisor)
            d.chiefSupervisorData = await queryEmployee(d.chiefSupervisor);
          return d;
        } catch (err) {
          console.log(err);
          return d;
        }
      })).then((results) => {
        return (results as PromiseFulfilledResult<any>[]).map((v) => v.value);
      }).then((data) => {
        setEmployees(data);
        setLoading(false);
        setFirstVisibleEmployee(querySnapShots.docs[0]);
        setLastVisibleEmployee(querySnapShots.docs[querySnapShots.docs.length - 1]);
      });
  };

  const isSelected = (name: string) => selected.indexOf(name) !== -1;

  const handleAddOnClick = () => {
    setSelected([]);
    setOpenEditForm(true);
  };

  const handleEditOnClick = (event: React.MouseEvent<HTMLElement>, idNumber: string) => {
    setEditMenuAnchorEl(event.currentTarget)
    setSelected([idNumber])
  };

  const handleEditMenuOnClose = () => {
    setEditMenuAnchorEl(null);
  };
  
  const handleEditInformationOnClick = () => {
    setEditMenuAnchorEl(null);
    setOpenEditForm(true);
  };

  const handleOpenResetAccountOnClick = () => {
    setEditMenuAnchorEl(null);
    setOpenResetAccount(true);
  };  

  const handleResetAccountOnClose = () => {
    setOpenResetAccount(false);
  };

  const handleOpenUpdatePhotoOnClick = () => {
    setEditMenuAnchorEl(null);
    setOpenUpdatePhoto(true);
  };  

  const handleUpdatePhotoOnClose = () => {
    setOpenUpdatePhoto(false);
  }

  const handlePhotoOnUpload = () => {
    handlePageOnChange(null, page);
  };

  const handleOpenTransitionEmployeeOnClick = () => {
    setEditMenuAnchorEl(null);
    setOpenTransitionEmployee(true);
  };  

  const handleTransitionOnClose = () => {
    setOpenTransitionEmployee(false);
  };

  const handleDeleteOnClick = (idNumber: string) => {
    setSelected([idNumber])
    setShowDeleteConfirm(true);
  };

  const handleDeleteOnConfirm = () => {
    const toDelete = [...selected];
    setShowDeleteConfirm(false);
    setEmployees(employees.filter((e) => !selected.includes(e.idNumber)));
    setSelected([]);

    toDelete.forEach((idNumber) => {
      setDoc(doc(db, `Employees/${idNumber}`), { taggedForDeletion: true, status: 'Inactive' }, { merge: true }).then(() => {
        // Refresh
        handlePageOnChange(null, page);
      });
    });
  };

  const handleEmployeeNameOnClick = (event: MouseEvent<HTMLButtonElement>, employee: any) => {
    event.stopPropagation();
    navigate(`/home/employees/${employee.idNumber}`, {
      state: {
        employee,
      }
    });
  };

  const handlePageOnChange = (event: React.MouseEvent<HTMLButtonElement> | null, to: number) => {
    let q: Query<Employee> | null = null;
    if (to > page) {
      // Next
      q = query(collection(db, 'Employees'), where('status', '==', 'Active'), orderBy('idNumber', 'asc'), startAfter(lastVisibleEmployee), limit(employeePerPage)).withConverter(employeeConverter);
    } else if (to < page) {
      // Prev
      q = query(collection(db, 'Employees'), where('status', '==', 'Active'), orderBy('idNumber', 'asc'), endBefore(firstVisibleEmployee), limitToLast(employeePerPage)).withConverter(employeeConverter);
    } else {
      // Same page
      q = query(collection(db, 'Employees'), where('status', '==', 'Active'), orderBy('idNumber', 'asc'), startAt(firstVisibleEmployee), limit(employeePerPage)).withConverter(employeeConverter);
    }

    q = query(
      q, 
      or(
        and(
          where('idNumber', '>=', search), 
          where('idNumber', '<=', search + '\uf8ff'),
        ),
        and(
          where('iqamaNumber', '==', search),
        ),
      )
    );

    getDocs(q).then((qSnapshot) => {
      setEmployeeList(qSnapshot).then(() => {
        setPage(to);
      });
    });
  };

  const handleRowsPerPageOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setEmployeePerPage(+event.target.value));
    setPage(0);
  };

  const fill = (value: string) => {
    const constraints = and(
      where('department', '==', 'AL NADAFA'),
      where('status', '==', 'Active'),
      or(
        and(
          where('idNumber', '>=', value), 
          where('idNumber', '<=', value + '\uf8ff'),
        ),
        and(
          where('iqamaNumber', '==', value),
        ),
      )
    );
    const a = query(
      collection(db, 'Employees'), 
      constraints,
      orderBy('idNumber', 'asc')
    ).withConverter(employeeConverter);
    const q = query(a, limit(employeePerPage));
    getDocs(q).then((qSnapshot) => setEmployeeList(qSnapshot));
    getCountFromServer(a).then(count => setTotalCount(count.data().count));
  };

  // Search functions
  const searchDebounced = useDebouncedCallback((value) => {
    fill(value);
  }, 1000);

  const handleSearchOnChanged = (evt: ChangeEvent<HTMLInputElement>) => {
    setSearch(evt.currentTarget.value);
    searchDebounced(evt.currentTarget.value);
  };

  useEffect(() => {
    fill(search);
  }, [employeePerPage]);

  return (
    <>
      <Paper elevation={4} sx={{ width: '100%', overflow: 'hidden' }}>
        <Box p={1} display="flex" flexDirection="row">
          <Box width={650} sx={{ marginInlineEnd: 1 }}>
            <Search>
              <SearchIconWrapper>
                <PersonSearchRounded color='primary' />
              </SearchIconWrapper>
              <StyledInputBase
                placeholder="Search active employee..."
                inputProps={{ 'aria-label': 'search' }}
                value={search}
                onChange={handleSearchOnChanged}
              />
            </Search>
          </Box>
          <Box flexGrow={1} />
          <SecureComponent permissions={['create.employees']}>
            <ButtonGroup sx={{ marginInlineEnd: 1 }} variant="contained" ref={anchorAddButtonRef} aria-label="split button">
              <Button 
                startIcon={<PersonAddRounded />} 
                onClick={handleAddOnClick} 
                sx={{ width: 200, justifyContent: 'left' }}
              >
                {addButtonOptions[selectedAddButtonAction]}
              </Button>
              <Button
                size="small"
                aria-controls={addButtonOpen ? 'split-button-menu' : undefined}
                aria-expanded={addButtonOpen ? 'true' : undefined}
                aria-label="select merge strategy"
                aria-haspopup="menu"
                onClick={() => setAddButtonOpen((prev) => !prev)}
              >
                <ArrowDropDownRounded />
              </Button>
            </ButtonGroup>
          </SecureComponent>
          <Popper
            sx={{
              zIndex: 9999,
            }}
            open={addButtonOpen}
            anchorEl={anchorAddButtonRef.current}
            role={undefined}
            transition
            disablePortal
          >
            {({ TransitionProps, placement }) => (
              <Grow
                {...TransitionProps}
                style={{
                  transformOrigin:
                    placement === 'bottom' ? 'center top' : 'center bottom',
                }}
              >
                <Paper>
                  <ClickAwayListener onClickAway={(event: Event) => {
                    if (anchorAddButtonRef.current && anchorAddButtonRef.current.contains(event.target as HTMLElement))
                      return;
                    setAddButtonOpen(false);
                  }}>
                    <MenuList id="split-button-menu" autoFocusItem>
                      {addButtonOptions.map((option, index) => (
                        <MenuItem
                          key={option}
                          selected={index === selectedAddButtonAction}
                          onClick={() => {
                            setSelectedAddButtonAction(index);
                            setAddButtonOpen(false);
                          }}
                          sx={{ width: 244 }}
                        >
                          {option}
                        </MenuItem>
                      ))}
                    </MenuList>
                  </ClickAwayListener>
                </Paper>
              </Grow>
            )}
          </Popper>
        </Box>
        <TableContainer>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell width={76} align="right">Count</TableCell>
                <TableCell align="left">Employee</TableCell>
                <TableCell width={180} align="left">Iqama Number</TableCell>
                <TableCell width={215} align="left">Occupation</TableCell>
                <TableCell width={90} align="center">
                  <Stack direction='row' spacing={1} justifyContent="center" alignItems="center">
                    <span>
                      Years
                    </span>
                    <Tooltip arrow title="Tenure/Last Vacations Years">
                      <InfoRounded sx={{cursor: 'pointer'}} fontSize="small" />
                    </Tooltip>
                  </Stack>
                </TableCell>
                <SecureComponent permissions={['update.employees', 'delete.employees']}>
                  <TableCell align="center">Actions</TableCell>
                </SecureComponent>
                <TableCell width={72} align="center"></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                  <TableCell colSpan={6}>
                    <Box sx={{ height: 300, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                      <CircularProgress />
                      <Typography variant='caption' sx={{ mt: 2 }}>Please wait...</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
              {employees.map((e, i) => {
                const dateHired = DateTime.fromFormat(e.dateHired, 'yyyyMMdd');
                const dateNow = DateTime.now();
                const interval = Interval.fromDateTimes(dateHired, dateNow);

                const tenure = e.metadata?.vacationInfo.vacation
                  ? e.metadata?.vacationInfo.years
                  : interval.length('years');

                return (
                  <HtmlTooltip key={e.uid + '-tooltip'} title={e.metadata?.vacationInfo.vacation ? (
                    <Stack p={1} spacing={1}>
                      <Stack direction='row' mb={1} alignItems="center" spacing={1}>
                        <VillaRounded />
                        <Typography variant='body1'>Last Vacation Information</Typography>
                      </Stack>
                      <Alert icon={<CalendarTodayRounded />} color='info'>
                        <Stack direction='row' alignItems="center" spacing={1}>
                          <Typography variant='caption'>
                            {"Start Date — "}
                            <strong>{DateTime.fromFormat(e.metadata.vacationInfo.vacation.dateStart, 'yyyyMMdd').toFormat('MMMM dd, yyyy')}</strong>
                          </Typography>
                        </Stack>
                        <Stack direction='row' alignItems="center" spacing={1}>
                          <Typography variant='caption'>
                            {"End Date — "}
                            <strong>{DateTime.fromFormat(e.metadata.vacationInfo.vacation.dateEnd, 'yyyyMMdd').toFormat('MMMM dd, yyyy')}</strong>
                          </Typography>
                        </Stack>
                      </Alert>
                      <Typography>{"Years Past — "}
                        <Typography variant='button' color={e.metadata.vacationInfo.years < 0 ? 'error' : 'default'}>
                          <strong>{e.metadata.vacationInfo.years.toFixed(2)}</strong>
                        </Typography>
                      </Typography>
                      {e.metadata.vacationInfo.eligible ? (
                        <Chip variant='outlined' label='Employee is eligible for a vacation.' color='success' />
                      ) : (
                        <Chip variant='outlined' label='Employee is not eligible to take a vacation.' color='error' />
                      )}
                    </Stack>
                  ) : (
                    <Stack p={1} spacing={1}>
                      <Stack direction='row' mb={1} alignItems="center" spacing={1}>
                        <VillaRounded />
                        <Typography variant='body1'>No Last Vacation Information</Typography>
                      </Stack>
                      {interval.length('years') >= 2 ? (
                        <Chip variant='outlined' label='Employee is eligible for a vacation.' color='success' />
                      ) : (
                        <Chip variant='outlined' label='Employee is not eligible to take a vacation.' color='error' />
                      )}
                    </Stack>
                  )}>
                    <TableRow
                      key={e.uid}
                      hover
                      tabIndex={-1}
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    >
                      <TableCell component="th" scope="row" align="right">{(page * employeePerPage) + i + 1}</TableCell>
                      <TableCell align="left">
                        <Box sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                        }}>
                          <Avatar alt={e.name} src={e.picture} sx={{ mr: 2 }} />
                          <Box id={e.idNumber}>
                            <Box display="flex" flexDirection="row" alignItems="center">
                              {e.taggedForDeletion && (
                                <Tooltip title="This employee is tagged for deletion.">
                                  <PersonRemoveRounded color="error" sx={{ mr: 1 }} />
                                </Tooltip>
                              )}
                              {e.taggedForDeletion ? (e.name) : <CleanButton onClick={(evt) => handleEmployeeNameOnClick(evt, e)}>{e.name}</CleanButton>}
                              {/* <Typography fontWeight={700}>
                                {e.taggedForDeletion ? (e.name) : (
                                  <Link href={`/home/employees/${e.idNumber}`}>{e.name}</Link>
                                )}
                              </Typography> */}
                            </Box>
                            <Typography variant="caption">{e.idNumber}</Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell align="left">{e.iqamaNumber}</TableCell>
                      <TableCell align="left">{e.occupation}</TableCell>
                      <TableCell align="center">
                        <Stack direction='row' spacing={1}>
                          <Typography color={tenure > 0 ? 'default' : 'error'}>
                            {Number(Math.abs(parseFloat(tenure))).toFixed(2)}
                          </Typography>
                          <Chip variant='outlined' color={e.metadata?.vacationInfo.vacation ? 'success' : 'info'} label={e.metadata?.vacationInfo.vacation ? 'Vacation' : 'Tenure'} size='small' />
                        </Stack>
                      </TableCell>
                      <SecureComponent permissions={['update.employees', 'delete.employees']}>
                        <TableCell>
                          <Stack direction='row' spacing={1} justifyContent='center'>
                            <SecureComponent permissions={PERMISSIONS_USERS}>
                              <Tooltip title='Send Message'>
                                <span>
                                  <IconButton 
                                    disabled={e.metadata?.messageThreads === undefined} 
                                    color='info'
                                    onClick={() => {
                                      const messageThreads = e.metadata?.messageThreads;
                                      
                                      let path = '/messages/t/start-conversation'
                                      if (messageThreads !== undefined && messageThreads.length > 0) {
                                        path = `/messages/t/${messageThreads[0].id}`
                                      }

                                      navigate(path, {
                                        state: { idNumber: e.idNumber }
                                      });
                                    }}
                                  >
                                    <ChatBubbleRounded />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </SecureComponent>
                            <SecureComponent permissions={['update.employees']}>
                              <Tooltip title='Edit'>
                                <span>
                                  <IconButton color='warning' onClick={event => handleEditOnClick(event, e.idNumber)}><EditRounded /></IconButton>
                                </span>
                              </Tooltip>
                            </SecureComponent>
                            <SecureComponent permissions={['delete.employees']}>
                              <Tooltip title='Delete'>
                                <span>
                                  <IconButton color='error' onClick={() => handleDeleteOnClick(e.idNumber)}><DeleteRounded /></IconButton>
                                </span>
                              </Tooltip>
                            </SecureComponent>
                          </Stack>
                        </TableCell>
                      </SecureComponent>
                      <TableCell>
                        <Tooltip title='View'>
                          <span>
                            <IconButton color='info' onClick={() => navigate(`/home/employees/${e.idNumber}`)}><ArrowForwardIosRounded /></IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  </HtmlTooltip>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          rowsPerPageOptions={[25, 50, 100]}
          colSpan={5}
          count={totalCount}
          page={page}
          rowsPerPage={employeePerPage}
          SelectProps={{
            inputProps: {
              'aria-label': 'rows per page',
            },
          }}
          onPageChange={handlePageOnChange}
          onRowsPerPageChange={handleRowsPerPageOnChange}
          ActionsComponent={TablePaginationActions} />
      </Paper>
      <SecureComponent permissions={['update.employees']}>
          <Menu 
            anchorEl={editMenuAnchorEl} 
            open={editMenuOpen}
            onClose={handleEditMenuOnClose}
            PaperProps={{
              elevation: 0,
              sx: {
                overflow: 'visible',
                filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                mt: 1.5,
                '& .MuiAvatar-root': {
                  width: 32,
                  height: 32,
                  ml: -0.5,
                  mr: 1,
                },
                '&:before': {
                  content: '""',
                  display: 'block',
                  position: 'absolute',
                  top: 0,
                  right: 14,
                  width: 10,
                  height: 10,
                  bgcolor: 'background.paper',
                  transform: 'translateY(-50%) rotate(45deg)',
                  zIndex: 0,
                },
              },
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <MenuItem key='emp-d-tbl-m-1' onClick={handleEditInformationOnClick}>
              <ListItemIcon>
                <PersonRounded />
              </ListItemIcon>
              Update Information
            </MenuItem>
            <Tooltip key='emp-d-tbl-m-2' title='Promote/demote employee'>
              <MenuItem onClick={handleOpenTransitionEmployeeOnClick}>
                <ListItemIcon>
                  <CompareArrowsRounded />
                </ListItemIcon>
                Transition
              </MenuItem>
            </Tooltip>
            <MenuItem key='emp-d-tbl-m-3' onClick={handleOpenUpdatePhotoOnClick}>
              <ListItemIcon>
                <FaceRounded />
              </ListItemIcon>
              Change Picture
            </MenuItem>
            <Divider key='emp-d-tbl-m-4' />
            <MenuItem key='emp-d-tbl-m-5' onClick={handleOpenResetAccountOnClick}>
              <ListItemIcon>
                <ManageAccountsRounded />
              </ListItemIcon>
              Reset User Account
            </MenuItem>
          </Menu>
        </SecureComponent>
        <SecureComponent permissions={['delete.employees']}>
          <DeleteDialog 
            open={showDeleteConfirm} 
            employees={employees.filter((e) => selected.includes(e.idNumber))}
            onCancel={() => setShowDeleteConfirm(false)} 
            onConfirm={handleDeleteOnConfirm}
          />
        </SecureComponent>
        <SecureComponent permissions={['create.employees', 'update.employees']}>
          <EmployeeForm 
            open={openEditForm} 
            employee={selected.length > 0 ? employees.find((e) => e.idNumber === selected[0]) : undefined}
            type={selectedAddButtonAction}
            onClose={() => setOpenEditForm(false)}
            onSave={(e) => {
              if (selected.length > 0) {
                const idx = employees.findIndex((e) => e.idNumber === selected[0]);
                if (idx !== -1 && e.idNumber === selected[0]) {
                  const copy = [...employees];
                  copy[idx] = e;
                  setEmployees(copy);
                }
              }
              setSelected([]);
              setOpenEditForm(false);
            }} />
        </SecureComponent>
        <SecureComponent permissions={['update.employees']}>
          <Dialog open={openUpdatePhoto} onClose={handleUpdatePhotoOnClose}>
            <DialogTitle>Update Employee Photo</DialogTitle>
            <DialogContent>
              {selected.length > 0 && employees.findIndex(e => e.idNumber === selected[0]) !== -1 ? (
                <EmployeeUpdateProfilePhoto 
                  employee={employees.find(e => e.idNumber === selected[0])!} 
                  onUpload={handlePhotoOnUpload}
                />
              ) : (
                <Box></Box>
              )}
            </DialogContent>
          </Dialog>
        </SecureComponent>
        <SecureComponent permissions={['update.employees']}>
          <Dialog open={openResetAccount} onClose={handleResetAccountOnClose}>
            <DialogTitle>Reset Employee Account</DialogTitle>
            <DialogContent>
              {selected.length > 0 && employees.findIndex(e => e.idNumber === selected[0]) !== -1 ? (
                <EmployeeResetUserAccount employee={employees.find(e => e.idNumber === selected[0])!} />
              ) : (
                <Box></Box>
              )}
            </DialogContent>
          </Dialog>
        </SecureComponent>
        <SecureComponent permissions={['update.employees']}>
          <Dialog 
            open={openTransitionEmployee} 
            onClose={handleTransitionOnClose}
            maxWidth='lg'
          >
            <DialogTitle>Promote/Demote Employee</DialogTitle>
            <Divider />
            <DialogContent sx={{ p: 0 }}>
              {selected.length > 0 && employees.findIndex(e => e.idNumber === selected[0]) !== -1 ? (
                <EmployeeTransitionForm employee={employees.find(e => e.idNumber === selected[0])!} onUpdate={(t, o) => {
                  const idx = employees.findIndex(v => v.idNumber === selected[0]);
                  const a = { ...employees[idx], type: t, occupation: o };
                  const c = [...employees];
                  c[idx] = a;
                  setEmployees(c);
                  handleTransitionOnClose();
                }} />
              ) : (
                <Box></Box>
              )}
            </DialogContent>
          </Dialog>
      </SecureComponent>
    </>
  );
};